import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from "@material-ui/core/styles";
import { useHistory } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import ConfirmDialog from '../../components/ConfirmDialog';
import { getBanners, deleteBanner } from '../../services/bannerService';
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, Chip, Box, makeStyles, CircularProgress, Snackbar, Tooltip
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import BannerFormModal from './BannerFormModal';


const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3) },
  tableContainer: { borderRadius: 16, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` },
  statusActive: { backgroundColor: 'rgba(16,185,129,0.15)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)', fontWeight: 600 },
  statusInactive: { backgroundColor: 'rgba(148,163,184,0.15)', color: theme.palette.text.secondary, border: `1px solid ${theme.palette.divider}`, fontWeight: 600 },
  actionBtn: { color: theme.palette.text.secondary, padding: 8, '&:hover': { backgroundColor: theme.palette.action.hover, color: theme.palette.text.primary } },
  deleteBtn: { color: theme.palette.text.secondary, padding: 8, '&:hover': { backgroundColor: 'rgba(239,68,68,0.1)', color: '#ef4444' } },
  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400 },
  shortText: { color: theme.palette.text.secondary, fontSize: '0.85rem', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  countChip: {
    background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.15) 100%)',
    color: '#10b981',
    border: '1px solid rgba(16,185,129,0.3)',
    fontWeight: 700,
    fontSize: '0.85rem',
    height: 36,
    padding: '0 8px',
    borderRadius: 10,
  } }));

function formatDate(timestamp) {
  if (!timestamp) return '-';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function BannerListPage() {
  const classes = useStyles();
  
  const theme = useTheme();
const history = useHistory();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedBannerId, setSelectedBannerId] = useState(null);

  const handleOpenCreateModal = () => {
    setSelectedBannerId(null);
    setFormModalOpen(true);
  };

  const handleOpenEditModal = (bannerId) => {
    setSelectedBannerId(bannerId);
    setFormModalOpen(true);
  };

  const handleCloseFormModal = () => {
    setFormModalOpen(false);
    setSelectedBannerId(null);
  };

  const handleFormSuccess = () => {
    setSnackbar({
      open: true,
      message: selectedBannerId ? 'ব্যানার সফলভাবে আপডেট করা হয়েছে!' : 'নতুন ব্যানার সফলভাবে তৈরি করা হয়েছে!'
    });
    loadData();
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getBanners();
      setItems(result);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load banners' });
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteBanner(deleteTarget.id);
      setSnackbar({ open: true, message: 'Banner deleted' });
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete' });
    }
  };

  const now = new Date();

  return (
    <AdminLayout title="Banner Management">
      <div className={classes.toolbar}>
        <Chip label={`Total Banners: ${items.length}`} className={classes.countChip} />
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenCreateModal} disableElevation>
          Add Banner
        </Button>
      </div>

      {loading ? (
        <div className={classes.loadingContainer}><CircularProgress size={40} /></div>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Short Text</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => {
                const start = item.startDate?.toDate ? item.startDate.toDate() : new Date(item.startDate);
                const end = item.endDate?.toDate ? item.endDate.toDate() : new Date(item.endDate);
                const isActive = now >= start && now <= end;
                return (
                  <TableRow key={item.id}>
                    <TableCell style={{ fontWeight: 600, color: theme.palette.text.primary }}>{item.title?.en || item.title || ''}</TableCell>
                    <TableCell><span className={classes.shortText}>{item.shortText?.en || item.shortText || ''}</span></TableCell>
                    <TableCell color="textSecondary">{formatDate(item.startDate)}</TableCell>
                    <TableCell color="textSecondary">{formatDate(item.endDate)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={isActive ? 'Active' : 'Inactive'} className={isActive ? classes.statusActive : classes.statusInactive} />
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end" style={{ gap: 6 }}>
                        <Tooltip title="Edit"><IconButton className={classes.actionBtn} onClick={() => handleOpenEditModal(item.id)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton className={classes.deleteBtn} onClick={() => setDeleteTarget(item)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" style={{ padding: "48px 0" }} color="textSecondary">No banners yet. Create your first one!</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmDialog open={!!deleteTarget} title="Delete Banner" message="Are you sure?" onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
      
      <BannerFormModal
        open={formModalOpen}
        bannerId={selectedBannerId}
        onClose={handleCloseFormModal}
        onSuccess={handleFormSuccess}
      />

      <Snackbar open={snackbar.open} message={snackbar.message} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </AdminLayout>
  );
}